import _ from "lodash";
/**--external-- */
import React, { Fragment } from "react";
import classNames from "classnames";
import { Button, IconButton, Dropdown, DropdownMenu } from "@toddle-edu/ds-web";
import update from "immutability-helper";
import {
  AddOutlined,
  DotsHorizontalOutlined,
  DeleteOutlined,
} from "@toddle-edu/ds-icons";

/**--internal-- */
import UIBaseComponent from "UIComponents/UIBaseComponent";
import I18nHOC from "UIComponents/I18nHOC";
import UIModal from "UIComponents/UIModal";
import { getElementValueForPortedFilteredByField } from "Fields/FieldUtils";
import Levels from "MultiLevelNodeEditor/routes/Levels/components";
import {
  generateRandomId,
  mergeDeep,
  getTopParent,
  getPYPElementSetFromNodes,
} from "Utils";
import { plannerElementsUnitPlanPropsMemoize } from "NodeEditor/Utils";
import { getElementValue } from "modules/ElementValueUtils";
import FieldComponent from "../FieldComponent";
/**--relative-- */
import classes from "./MultiWindowTable.scss";
import {
  nodeEditorConfigProp,
  getDropdownOptions,
} from "./MultiWindowTableUtils";
import {
  overlayStyle,
  containerStyle,
  editContainerStyle,
} from "./MultiWindowTableStyles";
import NestedChecklistEditor from "./components/NestedChecklistEditor";

class MultiWindowTable extends UIBaseComponent {
  constructor(props) {
    super(props);

    const { value } = props;

    this.state = {
      fieldKey: null,
      tableId: null,
      tableRowId: null,
    };

    if (_.isEmpty(value)) {
      const table = this.generateEmptyTable();

      this.updateInputFieldLocally({ value: table });
    }
  }

  updateInputFieldLocally = ({ value }) => {
    this.updateValue(value);
  };

  getFieldObj = () => {
    const { field_list, fieldKey } = this.props;
    return _.get(field_list, fieldKey, {});
  };

  getWindowConfigBySubField = ({ fieldKey }) => {
    const fieldObj = this.getFieldObj();
    const windowConfig = _.get(fieldObj, "config.windowConfig", []);

    return _.find(windowConfig, ({ fields }) => _.includes(fields, fieldKey));
  };

  getWindowConfigGroupedByWindowGroupId = () => {
    const fieldObj = this.getFieldObj();
    const windowConfig = _.get(fieldObj, "config.windowConfig", []);
    return _.groupBy(windowConfig, ({ windowGroupId }) => windowGroupId);
  };

  generateEmptyTable = () => {
    const tableId = generateRandomId();

    const tableGroupValues = {
      [tableId]: {
        tableRowsValues: [],
      },
    };

    return tableGroupValues;
  };

  shouldShowEmpty = () => {
    return false;
  };

  checkIfTableRowIsEmpty = ({ tableRow }) => {
    const { subFieldValues } = tableRow;

    return _.every(subFieldValues, ({ values, id }) => {
      const availableValues = this.getAvailableValues({ fieldKey: id, values });

      return _.isEmpty(availableValues);
    });
  };

  addTableRow = ({ windowGroupId, tableId, fieldKey }) => {
    const tableRowId = generateRandomId();

    const { value: tableGroupValues } = this.props;

    const windowConfigs = this.getWindowConfigGroupedByWindowGroupId()[
      windowGroupId
    ];

    const subFields = _.reduce(
      windowConfigs,
      (result, windowConfig) => {
        const fields = _.get(windowConfig, "fields", []);
        //If current window contains selected field, then select that field else select first field from all fields
        if (_.includes(fields, fieldKey)) {
          return [...result, fieldKey];
        }
        return [...result, _.first(fields)];
      },
      []
    );

    const subFieldInitialValues = _.map(subFields, key => ({
      id: key,
      values: [],
    }));

    const updatedTableGroupValues = update(tableGroupValues, {
      [tableId]: {
        tableRowsValues: {
          $push: [
            {
              id: tableRowId,
              subFieldValues: subFieldInitialValues,
              windowGroupId,
            },
          ],
        },
      },
    });

    this.updateInputFieldLocally({ value: updatedTableGroupValues });

    return tableRowId;
  };

  addFieldInTableRow = ({
    tableRowId,
    newFieldId,
    oldFieldId,
    values,
    tableId,
  }) => {
    const { value: tableGroupValues } = this.props;
    const tableRowIndex = this.getTableRowIndex({
      tableGroupValues,
      tableId,
      tableRowId,
    });
    const updatedTableGroupValues = update(tableGroupValues, {
      [tableId]: {
        tableRowsValues: {
          [tableRowIndex]: {
            subFieldValues: {
              $set: _.map(
                _.get(
                  tableGroupValues,
                  `${tableId}.tableRowsValues[${tableRowIndex}].subFieldValues`,
                  []
                ),
                ({ id: fieldKey, values: oldValues }) =>
                  fieldKey === oldFieldId
                    ? { id: newFieldId, values }
                    : { id: fieldKey, values: oldValues }
              ),
            },
          },
        },
      },
    });

    this.updateInputFieldLocally({ value: updatedTableGroupValues });
  };

  getAvailableValues = ({ fieldKey, values }) => {
    const subFieldConfig = this.getSubFieldConfig({ fieldKey });
    const isFilteredByFieldPorted = _.get(
      subFieldConfig,
      "config.isFilteredByFieldPorted"
    );

    const { getOptions, subjectIds } = this.props;

    const configOptions = _.get(subFieldConfig, "config.options", []);
    const fieldUid = _.get(subFieldConfig, "config.filteredBy[0]", null);

    const updatedOptions = getOptions({
      fieldKey: isFilteredByFieldPorted ? fieldUid : fieldKey,
      options: configOptions,
    });

    const resolvedMinimalTree = updatedOptions.resolvedMinimalTree ?? {};

    let benchmarkSet = {};
    if (_.get(subFieldConfig, "type", "IBBenchmarks")) {
      benchmarkSet = getPYPElementSetFromNodes({
        nodes: _.get(resolvedMinimalTree, fieldUid, []),
        startDepth: 1,
      });
    }

    const updatedValues = isFilteredByFieldPorted
      ? getElementValueForPortedFilteredByField({
          resolvedMinimalTree,
          value: values,
        })
      : getElementValue({
          valueKeys: values,
          fieldObj: subFieldConfig,
          resolvedValue: resolvedMinimalTree,
          subjectIds,
          fieldUID: fieldUid,
          benchmarkSet,
        });

    return updatedValues;
  };

  getSubFieldConfig = ({ fieldKey }) => {
    const { subFields } = this.props;

    return _.find(subFields, ({ key }) => key === fieldKey);
  };

  getTableRowIndex = ({ tableGroupValues, tableId, tableRowId }) => {
    const tableRows = _.get(tableGroupValues, `${tableId}.tableRowsValues`, []);

    return _.findIndex(tableRows, ({ id }) => id === tableRowId);
  };

  getFieldValue = ({ tableGroupValues, tableId, tableRowId, fieldKey }) => {
    const tableRowsValues = _.get(
      tableGroupValues,
      `${tableId}.tableRowsValues`,
      []
    );

    const currentTableRow = _.find(tableRowsValues, ({ id }) => {
      return tableRowId === id;
    });

    const subField = _.find(
      _.get(currentTableRow, "subFieldValues", []),
      ({ id }) => id === fieldKey
    );

    return _.get(subField, "values", []);
  };

  updateFieldModalVisibility = ({ key, tableRowId, tableId }) => {
    this.setState({ fieldKey: key, tableId, tableRowId });
  };

  getTableRowsValuesByWindowGroup = ({ tableRowsValues }) => {
    return _.groupBy(tableRowsValues, ({ windowGroupId }) => windowGroupId);
  };

  addTable = () => {
    const table = this.generateEmptyTable();

    const { value: tableGroupValues } = this.props;

    const updatedTableGroupValues = { ...tableGroupValues, ...table };

    this.updateInputFieldLocally({ value: updatedTableGroupValues });
  };

  deleteTable = ({ tableId }) => {
    const { value: tableGroupValues } = this.props;

    const updatedTableGroupValues = { ..._.omit(tableGroupValues, [tableId]) };

    this.updateInputFieldLocally({ value: updatedTableGroupValues });
  };

  addAndOpenFieldInstance = ({ windowGroupId, tableId, key }) => {
    const tableRowId = this.addTableRow({
      windowGroupId,
      tableId,
      fieldKey: key,
    });

    this.updateFieldModalVisibility({
      key,
      tableId,
      tableRowId,
    });
  };

  updateFieldInstance = ({ fieldKey, tableRowId, tableId, fieldValues }) => {
    const { value: tableGroupValues } = this.props;

    const tableRowIndex = this.getTableRowIndex({
      tableGroupValues,
      tableId,
      tableRowId,
    });

    const updatedValues = update(tableGroupValues, {
      [tableId]: {
        tableRowsValues: {
          [tableRowIndex]: {
            subFieldValues: {
              $set: _.map(
                _.get(
                  tableGroupValues,
                  `${tableId}.tableRowsValues[${tableRowIndex}].subFieldValues`,
                  []
                ),
                ({ id, values }) => {
                  if (id === fieldKey) {
                    return { id, values: fieldValues };
                  }
                  return { id, values };
                }
              ),
            },
          },
        },
      },
    });

    this.updateInputFieldLocally({ value: updatedValues });
  };

  deleteTableRow = ({ tableRowId, tableId }) => {
    const { value: tableGroupValues } = this.props;

    const updatedValues = update(tableGroupValues, {
      [tableId]: {
        tableRowsValues: {
          $set: _.filter(
            _.get(tableGroupValues, `${tableId}.tableRowsValues`, []),
            ({ id }) => id !== tableRowId
          ),
        },
      },
    });

    this.updateInputFieldLocally({ value: updatedValues });
  };

  deleteFieldInstance = ({ tableRowId, tableId, fieldKey }) => {
    const { value: tableGroupValues } = this.props;

    const tableRowIndex = this.getTableRowIndex({
      tableGroupValues,
      tableId,
      tableRowId,
    });

    const { subFieldValues } = _.get(
      tableGroupValues,
      `${tableId}.tableRowsValues.[${tableRowIndex}]`,
      {}
    );

    const otherSubFieldValues = _.filter(
      subFieldValues,
      ({ id }) => id !== fieldKey
    );

    const areOtherSubFieldValuesEmpty = _.every(
      otherSubFieldValues,
      ({ values, id }) => {
        const availableValues = this.getAvailableValues({
          values,
          fieldKey: id,
        });

        return _.isEmpty(availableValues);
      }
    );

    if (areOtherSubFieldValuesEmpty) {
      this.deleteTableRow({ tableRowId, tableId });
    } else {
      const updatedValues = update(tableGroupValues, {
        [tableId]: {
          tableRowsValues: {
            [tableRowIndex]: {
              subFieldValues: {
                $set: _.map(subFieldValues, ({ id, values }) => {
                  if (id !== fieldKey) {
                    return { id, values };
                  }
                  return { id, values: [] };
                }),
              },
            },
          },
        },
      });

      this.updateInputFieldLocally({ value: updatedValues });
    }
  };

  closeModal = () => {
    const { tableRowId, tableId } = this.state;

    const { value: tableGroupValues } = this.props;

    const tableRow = _.find(
      _.get(tableGroupValues, `${tableId}.tableRowsValues`, []),
      ({ id }) => id == tableRowId
    );

    const isTableRowEmpty = this.checkIfTableRowIsEmpty({ tableRow });

    if (isTableRowEmpty) {
      this.deleteTableRow({ tableRowId, tableId });
    }

    this.updateFieldModalVisibility({
      tableRowId: null,
      key: null,
      tableId: null,
    });
  };

  getNodeEditorConfigurationProps = ({
    values,
    plannerElementSetData,
    parentNode,
    resolvedMinimalTree,
  }) => {
    const { curriculumType } = this.props;

    const { nodes } = resolvedMinimalTree;
    let filteredValue = values;

    let filters = {};

    let filteredNodes = [];

    if (!_.isNull(plannerElementSetData.groupedByType)) {
      //Handled for Objectives in which they are groupedBy subject groups
      if (plannerElementSetData.type != plannerElementSetData.groupedByType) {
        filteredNodes = _.filter(
          nodes,
          nodeItem =>
            !!_.find(nodeItem.associatedParents, { id: parentNode.id })
        );

        filteredValue = _.filter(
          values,
          val => !!_.find(filteredNodes, { id: val })
        );
        filters = {
          associatedParents: [
            {
              ids: [parentNode.id],
              type: plannerElementSetData.groupedByType,
            },
          ],
        };
        if (parentNode.selectedObjectiveIds) {
          filters = {
            ...filters,
            parentNodes: parentNode.selectedObjectiveIds,
          };
        }
      }

      //Handled for ATLS in which they are groupedBy ATL root nodes
      // now also handles Nested Elements
      else if (!parentNode.isDummy) {
        filteredValue = _.filter(values, val => {
          const topParent = getTopParent({ nodeId: val, nodes });
          return topParent == parentNode.id;
        });

        filters = {
          parentNodes: [parentNode.id],
        };
      }
    }

    //Handled for Interdisciplinary Criteria in which they are groupedBy ATL root nodes
    if (plannerElementSetData.subType) {
      filters = { ...filters, subTypes: [plannerElementSetData.subType] };
    }

    //Merge filter with prop filters
    if (!_.isNil(plannerElementSetData.filters)) {
      filters = mergeDeep(plannerElementSetData.filters, filters);
    }

    const rootNode = {
      ...parentNode,
      subject: parentNode.isDummy ? null : parentNode.id,
    };

    const newNodeEditorProps = plannerElementsUnitPlanPropsMemoize({
      filters,
      nodes: _.filter(nodes, node => _.includes(filteredValue, node.id)),
      parentNode,
      curriculumType,
      plannerElementType: plannerElementSetData.type,
    });

    return {
      rootNode,
      newNodeEditorProps,
      filteredValue,
      filters,
    };
  };

  onActionsClick = ({ action, key, tableId, tableRowId }) => {
    switch (action) {
      case "EDIT": {
        this.updateFieldModalVisibility({
          key,
          tableId,
          tableRowId,
        });
        break;
      }
      case "DELETE": {
        this.deleteFieldInstance({ tableId, tableRowId, fieldKey: key });
        break;
      }
    }
  };

  getWindowLabel = ({ fields }) => {
    return _.join(
      _.map(fields, field => {
        const fieldObj = this.getSubFieldConfig({
          fieldKey: field,
        });
        return _.get(fieldObj, "config.label", "");
      }),
      "/"
    );
  };

  renderHeaderElement = ({ label }) => {
    const { t } = this.props;

    const headerLabelClasses = classNames(classes.headerLabel, "heading-4");

    return (
      <div className={classes.headerBaseContainer}>
        <h4 className={headerLabelClasses}>{label}</h4>
        <Button variant="progressive" onClick={this.closeModal}>
          {t("common:done")}
        </Button>
      </div>
    );
  };

  renderLevelsEditMode = ({
    label,
    fieldKey,
    values,
    tableId,
    tableRowId,
    fieldTypeConfig,
    emptyMessage,
    defaultOptions,
    resolvedMinimalTree,
  }) => {
    const { t } = this.props;

    const updateLearningStandards = values => {
      this.updateFieldInstance({
        fieldValues: values,
        fieldKey,
        tableRowId,
        tableId,
      });
    };

    const emptyTextAddSubgroupLocale = _.get(
      fieldTypeConfig,
      "emptyTextAddSubgroupLocale",
      ""
    );

    const { parentNodes, plannerElementSetData } = defaultOptions;

    const parentNode = _.first(parentNodes);

    const resolvedMinimalTreeNodes = resolvedMinimalTree?.nodes ?? [];

    const {
      rootNode,
      newNodeEditorProps,
      filteredValue,
      filters,
    } = this.getNodeEditorConfigurationProps({
      values,
      parentNode,
      plannerElementSetData,
      resolvedMinimalTree,
    });

    const fieldEmptyTextContainerClasses = classNames(
      "text-subtitle-1",
      classes.fieldEmptyTextContainer
    );

    return (
      <div className={classes.levelsWrapper}>
        {this.renderHeaderElement({ label })}
        <div className={classes.levelsContainer}>
          {_.isEmpty(resolvedMinimalTreeNodes) ? (
            <div className={fieldEmptyTextContainerClasses}>{emptyMessage}</div>
          ) : (
            <Levels
              emptyText={t(emptyTextAddSubgroupLocale, {
                label: parentNode.label,
              })}
              rootNode={rootNode}
              {...newNodeEditorProps}
              value={filteredValue}
              nodeType={plannerElementSetData.type}
              nodeParentType={plannerElementSetData.parentType}
              plannerElementSetConfig={{ ...plannerElementSetData, filters }}
              updateValueLocally={updateLearningStandards}
              isTagging
              hideHeader
              mode="edit"
              onBackClick={this.closeModal}
              showSearchHeader={false}
              nodeEditorConfigProp={nodeEditorConfigProp}
              useResolvedMinimalTreeNodes
              resolvedMinimalTreeNodes={resolvedMinimalTreeNodes}
            />
          )}
        </div>
      </div>
    );
  };

  renderLevelsForPypStandards = ({
    defaultOptions,
    resolvedMinimalTree,
    values,
    fieldKey,
    tableRowId,
    tableId,
    emptyMessage,
    label,
  }) => {
    const subjectId = _.get(defaultOptions, "subjects[0].value", null);
    const rootNode = _.get(defaultOptions, "subjects[0].benchmarkRootNode", {});
    const grades = _.get(defaultOptions, "grades", []);

    const subFieldConfig = this.getSubFieldConfig({ fieldKey });
    const fieldUid = _.get(subFieldConfig, "config.filteredBy[0]", null);

    const resolvedMinimalTreeNodes = _.get(resolvedMinimalTree, fieldUid, []);
    const benchmarkSet = getPYPElementSetFromNodes({
      nodes: resolvedMinimalTreeNodes,
    });
    const updatedNodes = _.get(benchmarkSet, "nodes", []);
    const selectedNodes = _.filter(
      updatedNodes,
      node => node.subject == subjectId
    );
    const subjectValue = _.filter(values, valueId =>
      _.includes(
        _.map(selectedNodes, selectedNode => selectedNode.id),
        valueId
      )
    );

    const fieldEmptyTextContainerClasses = classNames(
      "text-subtitle-1",
      classes.fieldEmptyTextContainer
    );

    const updateBenchmarks = values => {
      this.updateFieldInstance({
        fieldValues: values,
        fieldKey,
        tableRowId,
        tableId,
      });
    };

    return (
      <div className={classes.levelsWrapper}>
        {this.renderHeaderElement({ label })}
        <div className={classes.levelsContainer}>
          {_.isEmpty(resolvedMinimalTreeNodes) ? (
            <div className={fieldEmptyTextContainerClasses}>{emptyMessage}</div>
          ) : (
            <Levels
              subjectId={subjectId}
              value={subjectValue}
              isTagging
              hideHeader
              mode="edit"
              onBackClick={this.closeModal}
              rootNode={rootNode}
              nodeType="BENCHMARK"
              valueType="benchmarks"
              updateValueLocally={updateBenchmarks}
              nodeEditorConfigProp={nodeEditorConfigProp}
              grades={grades}
              selectedNodes={selectedNodes}
              selectedTaggedIds={_.map(selectedNodes, ({ id }) => id)}
              openedFrom="UNIT_PLAN"
              resolvedMinimalTreeNodes={resolvedMinimalTreeNodes}
            />
          )}
        </div>
      </div>
    );
  };

  renderNestedChecklistEditModal = ({
    fieldKey,
    values,
    tableId,
    tableRowId,
    headerLabel,
    plannerElementSetId,
    resolvedMinimalTree,
    emptyMessage,
  }) => {
    const nodes = _.get(resolvedMinimalTree, "nodes", []);
    const taggedNodesWithAncestors = _.filter(nodes, node =>
      _.includes(values, node.id)
    );

    const updateValue = values => {
      this.updateFieldInstance({
        fieldValues: values,
        fieldKey,
        tableRowId,
        tableId,
      });
    };

    const fieldEmptyTextContainerClasses = classNames(
      "text-subtitle-1",
      classes.fieldEmptyTextContainer
    );

    return (
      <Fragment>
        {this.renderHeaderElement({ label: headerLabel })}
        {_.isEmpty(nodes) ? (
          <div className={fieldEmptyTextContainerClasses}>{emptyMessage}</div>
        ) : (
          <NestedChecklistEditor
            plannerElementSetId={plannerElementSetId}
            nodes={nodes}
            value={values}
            taggedNodesWithAncestors={taggedNodesWithAncestors}
            updateValue={updateValue}
          />
        )}
      </Fragment>
    );
  };

  renderFieldEditMode = ({ tableRowId, fieldKey, tableId, subField }) => {
    const {
      label,
      fieldTypeConfig,
      options: configOptions,
      tagOptions = [],
      subHeaderLabels = [],
      isFilteredByFieldPorted,
    } = _.get(subField, "config", {});
    const filteredBy = _.get(subField, "config.filteredBy[0]");

    const { type } = subField;

    const { value: tableGroupValues, t, getOptions } = this.props;

    const values = this.getFieldValue({
      tableGroupValues,
      tableId,
      tableRowId,
      fieldKey,
    });

    const updatedOptions = getOptions({
      options: configOptions,
      defaultOptions: configOptions,
      fieldKey: isFilteredByFieldPorted ? filteredBy : fieldKey,
    });

    if (isFilteredByFieldPorted) {
      return this.renderNestedChecklistEditModal({
        fieldKey,
        values,
        tableId,
        tableRowId,
        headerLabel: label,
        plannerElementSetId: _.get(updatedOptions, "plannerElementSetId"),
        resolvedMinimalTree: updatedOptions.resolvedMinimalTree,
        emptyMessage: t(`unitPlan:no_unit_plan_element_selected_with_label`, {
          label,
        }),
      });
    }

    switch (type) {
      case "NestedSelectComponent": {
        return this.renderLevelsEditMode({
          label,
          fieldKey,
          values,
          tableId,
          tableRowId,
          fieldTypeConfig,
          defaultOptions: updatedOptions,
          resolvedMinimalTree: updatedOptions.resolvedMinimalTree,
          emptyMessage: t(`unitPlan:no_unit_plan_element_selected_with_label`, {
            label,
          }),
        });
      }

      case "IBBenchmarks": {
        return this.renderLevelsForPypStandards({
          defaultOptions: updatedOptions,
          resolvedMinimalTree: updatedOptions.resolvedMinimalTree,
          values,
          fieldTypeConfig,
          fieldKey,
          tableRowId,
          tableId,
          label,
          emptyMessage: t(`unitPlan:no_unit_plan_element_selected_with_label`, {
            label,
          }),
        });
      }

      default: {
        const updateFieldInstance = ({ params: values }) => {
          this.updateFieldInstance({
            fieldValues: values,
            fieldKey,
            tableRowId,
            tableId,
          });
        };

        return (
          <div className={classes.inputFieldContainer}>
            {this.renderHeaderElement({ label })}
            <div className={classes.bodyContainer}>
              <FieldComponent
                type={type}
                value={values}
                updateInputField={updateFieldInstance}
                tagOptions={tagOptions}
                subHeaderLabels={subHeaderLabels}
                containerStyle={editContainerStyle}
              />
            </div>
          </div>
        );
      }
    }
  };

  renderModal = () => {
    const { fieldKey, tableRowId, tableId } = this.state;

    const subField = this.getSubFieldConfig({ fieldKey });

    const modalStyles = _.get(subField, "config.styles.modalStyles", {});

    return (
      <UIModal
        isOpen
        customStyle={{ content: modalStyles, overlay: overlayStyle }}
      >
        {this.renderFieldEditMode({
          tableRowId,
          fieldKey,
          tableId,
          subField,
        })}
      </UIModal>
    );
  };

  renderAddOption = ({ fields, dropdownOptions, disabled, onAddClick }) => {
    const { t } = this.props;

    const onDropdownClick = e => {
      e.domEvent.stopPropagation();
      const { key } = e;
      onAddClick({ key });
    };

    const overlayElement = (
      <DropdownMenu
        onClick={onDropdownClick}
        options={_.map(dropdownOptions, ({ id, label }) => ({
          key: id,
          label,
        }))}
      />
    );

    return _.size(fields) > 1 ? (
      <Dropdown
        getPopupContainer={_.identity}
        overlay={overlayElement}
        placement="bottomLeft"
        disabled={disabled}
      >
        <Button variant="outlined-subtle" icon={<AddOutlined />}>
          {t("common:add")}
        </Button>
      </Dropdown>
    ) : (
      <Button
        variant="outlined-subtle"
        icon={<AddOutlined />}
        onClick={() => onAddClick({ key: _.first(fields) })}
        disabled={disabled}
      >
        {t("common:add")}
      </Button>
    );
  };

  renderWindow = ({
    fieldKey,
    values,
    resolvedMinimalTree,
    defaultOptions,
    isAddNewWindowDisabled,
    tableId,
    tableRowId,
  }) => {
    const windowConfig = this.getWindowConfigBySubField({ fieldKey });
    const {
      showCellSubHeader = false,
      fields = [],
      dropdownOptions = [],
    } = windowConfig;
    const editStyle = _.get(windowConfig, "styles.editStyle", {});

    const { t } = this.props;

    const availableValues = this.getAvailableValues({ fieldKey, values });

    const onAddClick = ({ key }) => {
      if (key !== fieldKey) {
        this.addFieldInTableRow({
          tableRowId,
          newFieldId: key,
          oldFieldId: fieldKey,
          values: [],
          tableId,
        });
      }
      this.onActionsClick({ action: "EDIT", key, tableId, tableRowId });
    };

    const onDropdownClick = e => {
      e.domEvent.stopPropagation();
      const action = e.key;
      this.onActionsClick({
        action,
        key: fieldKey,
        tableId,
        tableRowId,
      });
    };

    const fieldObj = this.getSubFieldConfig({ fieldKey });
    const { config, viewType } = fieldObj;
    const { label: fieldLabel, fieldTypeConfig } = config;

    const nodes = _.get(resolvedMinimalTree, "nodes", []);

    return (
      <div className={classes.windowContainer} style={editStyle}>
        {!_.isEmpty(availableValues) ? (
          <React.Fragment>
            <div className={classes.fieldContainer}>
              {showCellSubHeader && (
                <div className="text-label-3">{fieldLabel}</div>
              )}
              <FieldComponent
                {..._.omit(config, ["label"])}
                containerStyle={containerStyle}
                fieldTypeConfig={fieldTypeConfig}
                value={availableValues}
                type={viewType}
                defaultOptions={defaultOptions}
                mode="view"
                nodes={nodes}
              />
            </div>
            <Dropdown
              getPopupContainer={_.identity}
              overlay={
                <DropdownMenu
                  onClick={onDropdownClick}
                  options={getDropdownOptions({ t })}
                />
              }
              placement={"bottomRight"}
            >
              <IconButton
                onClick={e => {
                  e.stopPropagation();
                }}
                icon={
                  <DotsHorizontalOutlined
                    variant={"subtle"}
                    size={"xx-small"}
                  />
                }
                variant="plain"
              />
            </Dropdown>
          </React.Fragment>
        ) : (
          this.renderAddOption({
            fields,
            dropdownOptions,
            disabled: isAddNewWindowDisabled,
            onAddClick,
          })
        )}
      </div>
    );
  };

  renderTable = ({ tableId, tableRowsValues, isDeleteTableDisabled }) => {
    const { getOptions } = this.props;

    const cellHeaderClasses = classNames(classes.cellHeader, "text-label-3");

    const tableRowsValuesByWindowGroup = this.getTableRowsValuesByWindowGroup({
      tableRowsValues,
    });

    const windowConfigsGroupedByWindowGroupId = this.getWindowConfigGroupedByWindowGroupId();

    const windowGroupIds = _.keys(windowConfigsGroupedByWindowGroupId);

    return (
      <div className={classes.tableWrapper}>
        <div className={classes.container}>
          {_.map(windowGroupIds, windowGroupId => {
            const tableRowsValues = tableRowsValuesByWindowGroup[windowGroupId];

            const totalRows = _.size(tableRowsValues);

            const windowConfigs =
              windowConfigsGroupedByWindowGroupId[windowGroupId];

            return (
              <div
                className={classes.tableRow}
                key={`${windowGroupId}-${tableId}`}
              >
                <div className={classes.tableRowHeader}>
                  {_.map(windowConfigs, windowConfig => {
                    const editStyle = _.get(
                      windowConfig,
                      "styles.editStyle",
                      {}
                    );

                    const fields = _.get(windowConfig, "fields", []);
                    const label = this.getWindowLabel({ fields });

                    return (
                      <div
                        className={cellHeaderClasses}
                        style={editStyle}
                        key={`header-${windowConfig.id}`}
                      >
                        {label}
                      </div>
                    );
                  })}
                </div>
                {_.map(
                  tableRowsValues,
                  ({ id: tableRowId, subFieldValues }, index) => {
                    return (
                      <div key={tableRowId} className={classes.tableRowContent}>
                        {_.map(subFieldValues, ({ id, values }) => {
                          const fieldConfig = this.getSubFieldConfig({
                            fieldKey: id,
                          });
                          const isFilteredByFieldPorted = _.get(
                            fieldConfig,
                            "config.isFilteredByFieldPorted"
                          );
                          const filteredBy = _.get(
                            fieldConfig,
                            "config.filteredBy[0]"
                          );
                          const configOptions = _.get(
                            fieldConfig,
                            "config.options",
                            []
                          );
                          const updatedOptions = getOptions({
                            fieldKey: isFilteredByFieldPorted ? filteredBy : id,
                            options: configOptions,
                          });

                          const windowConfig = this.getWindowConfigBySubField({
                            fieldKey: id,
                          });
                          const areMultipleCellsAllowed = _.get(
                            windowConfig,
                            "allowMultiple",
                            false
                          );

                          return (
                            <React.Fragment key={id}>
                              {this.renderWindow({
                                fieldKey: id,
                                values,
                                resolvedMinimalTree:
                                  updatedOptions.resolvedMinimalTree,
                                defaultOptions: updatedOptions,
                                isAddNewWindowDisabled:
                                  index !== 0
                                    ? false
                                    : !areMultipleCellsAllowed,
                                tableRowId,
                                tableId,
                              })}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    );
                  }
                )}
                <div className={classes.addFieldSection}>
                  {_.map(windowConfigs, windowConfig => {
                    const editStyle = _.get(
                      windowConfig,
                      "styles.editStyle",
                      {}
                    );
                    const areMultipleCellsAllowed = _.get(
                      windowConfig,
                      "allowMultiple",
                      false
                    );

                    const fields = _.get(windowConfig, "fields", []);

                    const dropdownOptions = _.get(
                      windowConfig,
                      "dropdownOptions",
                      []
                    );

                    const onAddClick = ({ key }) => {
                      this.addAndOpenFieldInstance({
                        windowGroupId,
                        tableId,
                        key,
                      });
                    };

                    return (
                      <div
                        className={classes.addButtonContainer}
                        key={`footer-${windowConfig.id}`}
                        style={editStyle}
                      >
                        {this.renderAddOption({
                          fields,
                          disabled: areMultipleCellsAllowed
                            ? false
                            : totalRows >= 1,
                          dropdownOptions,
                          onAddClick,
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <IconButton
          icon={<DeleteOutlined />}
          variant="outlined-subtle"
          size="medium"
          disabled={isDeleteTableDisabled}
          onClick={() => this.deleteTable({ tableId })}
        />
      </div>
    );
  };

  renderView = () => {
    const { value, subFields, mode } = this.props;

    return (
      <div>
        {_.map(subFields, subField => {
          const { viewType, key } = subField;

          const subFieldValue = value[key];

          return (
            <div key={key}>
              <FieldComponent
                type={viewType}
                value={subFieldValue}
                mode={mode}
              />
            </div>
          );
        })}
      </div>
    );
  };

  renderEdit = () => {
    const { tableId } = this.state;

    const { value: tableGroupValues, t } = this.props;

    const tableIds = _.keys(tableGroupValues);

    const totalTables = _.size(tableIds);

    const isDeleteTableDisabled = totalTables === 1;

    return (
      <div className={classes.tableGroupsContainer}>
        {_.map(tableIds, tableId => {
          const { tableRowsValues } = tableGroupValues[tableId];

          return (
            <React.Fragment key={tableId}>
              {this.renderTable({
                tableId,
                tableRowsValues,
                isDeleteTableDisabled,
              })}
            </React.Fragment>
          );
        })}
        <div>
          <Button
            size="large"
            variant="outlined-subtle"
            onClick={this.addTable}
            icon={<AddOutlined />}
          >
            {t("common:add_another_table")}
          </Button>
        </div>
        {tableId && this.renderModal()}
      </div>
    );
  };
}

export default I18nHOC({ resource: ["common", "unitPlan"] })(MultiWindowTable);
